import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { useToast } from '@chakra-ui/react';
import { useContext } from 'react';
import axios from 'axios';
import { ContractView, EquipmentType, Pagination, SelectItem } from '~/types';
import api from '~/utils/api';
import {
  MachinePark,
  MachineParkContractDetail,
  MachineParkContractDetailView,
} from '~/types/strategicMap';
import { PAGE_SIZE } from '~/constants';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { getContracts } from '~/services';
import { formatCurrency } from '~/utils/formatCurrency';
import { formatInTimeZoneBr } from '~/utils/formatToTimeZone';
import { getMonthByNumber } from '~/utils/getMonthByNumber';
import { formataMesStrAno } from '~/utils/formatDate';

export type Manufacturer = {
  id: number;
  nome: string;
  ativo: boolean;
};

type GetManufacturerResponse = {
  count: number;
  next: boolean | null;
  previous: boolean | null;
  results: Manufacturer[];
};

export async function getManufacturerAsSelect(): Promise<SelectItem[]> {
  const { data } = await api.get<GetManufacturerResponse>('/fabricantes');
  const list = data.results
    .filter(item => Boolean(Number(item.ativo)) === true)
    .map(m => ({
      value: String(m.id),
      label: m.nome,
    }));
  return list;
}

export function useManufacturerAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['fabricantes'],
    () => getManufacturerAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export async function getEquipamentTypeAsSelect(): Promise<SelectItem[]> {
  const { data } = await api.get<Pagination<EquipmentType>>(
    '/tipoequipamento',
    {
      params: {
        page: 1,
        size: 9999,
      },
    },
  );
  const list = data.results
    .filter(type => type.tp_situacao === '1')
    .map(type => ({
      label: type.nm_tipoequipamento || '-',
      value: String(type.id_tipoequipamento),
    }));

  return list.sort((a, b) => a.label.localeCompare(b.label));
}

export function useEquipmentTypesAsSelect(): UseQueryResult<
  SelectItem[],
  unknown
> {
  return useQuery<SelectItem[], unknown>(
    ['tipo_equipamentos'],
    () => getEquipamentTypeAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  );
}

export async function getMachineParkList(
  id_grupo: number,
  page: number,
): Promise<Pagination<MachinePark>> {
  const { data } = await api.get<Pagination<MachinePark>>('/parque_maquinas', {
    params: { id_grupo, page, size: PAGE_SIZE },
  });
  return data;
}

export async function getTotalMachineParkList(
  id_grupo: number,
): Promise<number> {
  const { data } = await api.get<number>(
    '/parque_maquinas/get_total_parque_maquinas',
    {
      params: { id_grupo },
    },
  );
  return data;
}

export async function getPieChartEquipmentGeral(
  id_grupo: number | undefined,
): Promise<any[]> {
  const { data } = await api.get(
    '/parque_maquinas_dashboard/chart_equipamentos_geral_positivo',
    {
      params: { id_grupo },
    },
  );
  return data.pieChart;
}

export function usePieChartEquipmentGeral(
  id_grupo: number | undefined,
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<any[], unknown>(
    [
      'piechartequipmentgeral',
      id_grupo,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () => getPieChartEquipmentGeral(id_grupo),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_grupo,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getDonutChartEquipmentCompeting(
  id_grupo: number | undefined,
): Promise<any[]> {
  const { data } = await api.get(
    '/parque_maquinas_dashboard/chart_equipamentos_concorrentes',
    {
      params: { id_grupo },
    },
  );
  return data.pieChart;
}

export function useDonutChartEquipmentCompeting(
  id_grupo: number | undefined,
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<any[], unknown>(
    [
      'donutchartequipmentcompeting',
      id_grupo,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () => getDonutChartEquipmentCompeting(id_grupo),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_grupo,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getDonutChartEquipmentPositivoAndCompeting(
  id_grupo: number | undefined,
): Promise<any[]> {
  const { data } = await api.get(
    '/parque_maquinas_dashboard/chart_positivo_e_concorrentes',
    {
      params: { id_grupo },
    },
  );
  return data.pieChart;
}

export function useDonutChartEquipmentPositivoAndCompeting(
  id_grupo: number | undefined,
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<any[], unknown>(
    [
      'donutchartequipmentpositivoandcompeting',
      id_grupo,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () => getDonutChartEquipmentPositivoAndCompeting(id_grupo),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_grupo,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useGroupContracts(
  id_grupo: number | undefined,
  search: string,
  page: number,
  pageSize = '10',
  options: UseQueryOptions<Pagination<ContractView>, unknown> = {},
): UseQueryResult<Pagination<ContractView>> {
  const toast = useToast();

  return useQuery<Pagination<ContractView>, unknown>(
    ['mapa_estrategico_contratos', id_grupo, page, search, pageSize],
    () =>
      getContracts(
        page,
        search,
        '1',
        'dt_encerramentocontrato',
        'DESC',
        '',
        '',
        '',
        {
          groups: [id_grupo?.toString()],
          headoffices: [],
          branches: [],
        },
        undefined,
        undefined,
        undefined,
        Number(pageSize),
      ),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_grupo,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function removeMachinePark(id: number): Promise<any> {
  return api.delete(`/parque_maquinas/${id}`);
}

export async function getBarChartEquipmentPositivo(
  id_grupo: number | undefined,
): Promise<any[]> {
  const { data } = await api.get(
    '/parque_maquinas_dashboard/idade_equipamentos_positivo',
    {
      params: { id_grupo },
    },
  );
  return formatDataForGoogleChartsWithTooltips(data.barChart);
}

export function useBarChartEquipmentPositivo(
  id_grupo: number | undefined,
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<any[], unknown>(
    [
      'barchartequipmentpositivo',
      id_grupo,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () => getBarChartEquipmentPositivo(id_grupo),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_grupo,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getBarChartEquipmentConcorrentes(
  id_grupo: number | undefined,
): Promise<any[]> {
  const { data } = await api.get(
    '/parque_maquinas_dashboard/idade_equipamentos_concorrentes',
    {
      params: { id_grupo },
    },
  );
  return formatDataForGoogleChartsWithTooltips(data.barChart);
}

export function useBarChartEquipmentConcorrentes(
  id_grupo: number | undefined,
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<any[], unknown>(
    [
      'barchartequipmentconcorrentes',
      id_grupo,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () => getBarChartEquipmentConcorrentes(id_grupo),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_grupo,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getDetalheContratoParqueMaquinas(
  id: number,
): Promise<MachineParkContractDetailView> {
  const { data } = await api.get<MachineParkContractDetail>(
    `mapa_estrategico_contratos/get_detalhe_contrato`,
    {
      params: { id },
    },
  );
  const contract = addFormattedPropertiesToMachineParkContract(data);

  return contract;
}

export function addFormattedPropertiesToMachineParkContract(
  contract: MachineParkContractDetail,
): MachineParkContractDetailView {
  const contractDueDate = new Date(`${contract.vencimento_final}T00:00:00Z`);

  return {
    ...contract,
    formattedMensalValue: formatCurrency(Number(contract.vr_mensal_atual)),
    formattedInitialDate: formatInTimeZoneBr(
      `${contract.dt_inicialcontrato}T00:00:00Z`,
      'MMM/yyyy',
    ),
    formattedEndDate: formatInTimeZoneBr(
      `${contract.vencimento_final}T00:00:00Z`,
      'MMM/yyyy',
    ),
    formattedMonth:
      Number(contract?.mm_ajusteinflacao) === 0 || !contract?.mm_ajusteinflacao
        ? '-'
        : getMonthByNumber(Number(contract?.mm_ajusteinflacao) - 1),
    colorScheme:
      contract?.status_pgto === 'Encerrado'
        ? 'purple'
        : contract?.status_pgto === 'Em Dia'
        ? 'green'
        : 'red',
    status_pgto: contract?.status_pgto,
    formattedVrPagoAteMomento: formatCurrency(
      Number(contract.vr_pago_ate_momento),
    ),
    formattedVrPendentePagamento: formatCurrency(
      Number(contract.vr_pendente_pagamento),
    ),
  };
}

const formatDataForGoogleChartsWithTooltips = (data: any[]) => {
  const formattedData = [];

  const headers = [];
  headers.push({ type: 'string', label: 'tipo' });

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < data[0].length; i++) {
    headers.push({ type: 'number', label: data[0][i] });
    headers.push({ type: 'string', role: 'tooltip' });
  }

  formattedData.push(headers);

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < data.length; i++) {
    const row = [];
    row.push(data[i][0]);

    // eslint-disable-next-line no-plusplus
    for (let j = 1; j < data[i].length; j++) {
      row.push(data[i][j]);
      row.push(
        `${data[i][0]} - ${data[0][j]}: ${data[i][j].toLocaleString('pt-BR')}`,
      );
    }

    formattedData.push(row);
  }

  return formattedData;
};

export function useCDIChart(
  id_contrato: number,
  data_inicio: string,
  data_extensoes: string[],
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  return useQuery<any[], unknown>(
    ['CDIChart', id_contrato],
    () => getCDIChart(id_contrato, data_inicio, data_extensoes),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_contrato,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getCDIChart(
  id: number,
  data_inicio: string,
  data_extensoes: string[],
): Promise<any[]> {
  const { data } = await api.get(
    '/mapa_estrategico_contratos/get_dados_grafico_cdi',
    {
      params: { id },
    },
  );
  return formatDataForCDIChart(data.chart, data_inicio, data_extensoes);
}

export function useIndexChart(
  id_contrato: number,
  data_inicio: string,
  data_extensoes: string[],
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  return useQuery<any[], unknown>(
    ['IndexChart', id_contrato],
    () => getIndexChart(id_contrato, data_inicio, data_extensoes),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_contrato,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getIndexChart(
  id: number,
  data_inicio: string,
  data_extensoes: string[],
): Promise<any[]> {
  const { data } = await api.get(
    '/mapa_estrategico_contratos/get_dados_grafico_indice_correcao_monetaria',
    {
      params: { id },
    },
  );
  return formatDataForCDIChart(data.chart, data_inicio, data_extensoes);
}

const formatDataForCDIChart = (
  data: any[],
  data_inicio: string,
  data_extensoes: string[],
) => {
  const formattedData = [];
  const headers = [
    { type: 'string', label: 'x' },
    { type: 'number', label: 'values' },
    { type: 'number', role: 'interval' }, // Intervalo inferior
    { type: 'number', role: 'interval' }, // Intervalo superior
    { type: 'string', role: 'tooltip', p: { html: true } }, // Tooltip customizado
    { type: 'string', role: 'style' }, // Cor do ponto
    { type: 'string', role: 'annotation' }, // Valor do ponto
  ];
  formattedData.push(headers);
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < data.length; i++) {
    const row = [];
    row.push(data[i][0]);
    // eslint-disable-next-line no-plusplus
    for (let j = 1; j < data[i].length; j++) {
      row.push(data[i][j]);
    }
    const tooltipValue = `<div style="width:80px"><span style="font-size: 10px; font-weight: bold">${
      data[i][0]
    }</span><br><span style="font-size: 10px">${data[i][1].replace(
      '.',
      ',',
    )}%</span></div>`;
    const pointColor =
      data_inicio.toLowerCase() === data[i][0].toLowerCase()
        ? 'point { size: 8; shape-type: circle; fill-color: #1BA65B; }'
        : data_extensoes.includes(data[i][0].toLowerCase())
        ? 'point { size: 8; shape-type: circle; fill-color: #FC8E0E; }'
        : 'point { size: 3; shape-type: circle; fill-color: #4341C5; }';
    row.push(tooltipValue);
    row.push(pointColor);
    row.push(data[i][1].replace('.', ',').concat('%'));

    formattedData.push(row);
  }
  return formattedData;
};

export function useAccumulatedPaymentChart(
  id_contrato: number,
  data_inicio: string,
  data_extensoes: string[],
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  return useQuery<any[], unknown>(
    ['AccumulatedPaymentChart', id_contrato],
    () => getAccumulatedChart(id_contrato, data_inicio, data_extensoes),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_contrato,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export async function getAccumulatedChart(
  id: number,
  data_inicio: string,
  data_extensoes: string[],
): Promise<any[]> {
  const { data } = await api.get(
    '/mapa_estrategico_contratos/get_dados_grafico_pag_acumulado',
    {
      params: { id },
    },
  );
  return formatDataForAccumulatedPaymentChart(
    data.chart,
    data_inicio,
    data_extensoes,
  );
}
const formatDataForAccumulatedPaymentChart = (
  data: any[],
  data_inicio: string,
  data_extensoes: string[],
) => {
  if (data.length === 0) {
    return [];
  }
  const formattedData = [];
  const headers = [
    { type: 'string', label: 'x' },
    { type: 'number', label: 'values' },
    { type: 'string', role: 'tooltip', p: { html: true } }, // Tooltip customizado
    { type: 'string', role: 'style' }, // Cor do ponto
    { type: 'string', role: 'annotation' }, // Valor do ponto
  ];
  formattedData.push(headers);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < data.length; i++) {
    const row = [];
    row.push(data[i][0]);
    // eslint-disable-next-line no-plusplus
    for (let j = 1; j < data[i].length; j++) {
      row.push(data[i][j]);
    }
    const tooltipValue = `<div style="width:80px"><span style="font-size: 10px; font-weight: bold">${
      data[i][0]
    }</span><br><span style="font-size: 10px">${formatCurrency(
      Number(data[i][1]),
    )}</span></div>`;
    const pointColor =
      data_inicio.toLowerCase() === data[i][0].toLowerCase()
        ? 'point { size: 8; shape-type: circle; fill-color: #1BA65B; }'
        : data_extensoes.includes(data[i][0].toLowerCase())
        ? 'point { size: 8; shape-type: circle; fill-color: #FC8E0E; }'
        : 'point { size: 3; shape-type: circle; fill-color: #2c8ad2; }';
    row.push(tooltipValue);
    row.push(pointColor);
    row.push(formatCurrency(Number(data[i][1])));
    formattedData.push(row);
  }
  return formattedData;
};

export function useDolarChart(
  id_contrato: number,
  data_inicio: string,
  options: UseQueryOptions<DolarChartResponse, unknown> = {},
): UseQueryResult<DolarChartResponse> {
  const toast = useToast();
  return useQuery<DolarChartResponse, unknown>(
    ['DolarChart', id_contrato],
    () => getDolarChart(id_contrato, data_inicio),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      enabled: !!id_contrato,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

interface DolarChartResponse {
  dolar_hoje: {
    data: string;
    valor: string;
  };
  grafico_anterior: any[];
  grafico_atual: any[];
}

export async function getDolarChart(
  id: number,
  data_inicio: string,
): Promise<DolarChartResponse> {
  const { data } = await api.get<{ chart: DolarChartResponse }>(
    '/mapa_estrategico_contratos/get_dados_grafico_dolar',
    {
      params: { id },
    },
  );
  return {
    dolar_hoje: data.chart.dolar_hoje,
    grafico_anterior: formatDataForDolarChart(
      data.chart.grafico_anterior,
      data_inicio,
    ),
    grafico_atual: formatDataForDolarChart(
      data.chart.grafico_atual,
      data_inicio,
    ),
  };
}

const formatDataForDolarChart = (data: any[], data_inicio: string) => {
  const formattedData = [];
  const headers = [
    { type: 'string', label: 'x' },
    { type: 'number', label: 'values' },
    { type: 'string', role: 'tooltip', p: { html: true } }, // Tooltip customizado
    { type: 'string', role: 'style' }, // Cor do ponto
    { type: 'string', role: 'annotation' }, // Valor do ponto
  ];
  formattedData.push(headers);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < data.length; i++) {
    const row = [];
    row.push(data[i][0]);
    // eslint-disable-next-line no-plusplus
    for (let j = 1; j < data[i].length; j++) {
      row.push(data[i][j]);
    }
    const tooltipValue = `<div style="width:80px"><span style="font-size: 10px; font-weight: bold">${
      data[i][0]
    }</span><br><span style="font-size: 10px">${formatCurrency(
      Number(data[i][1]),
    )}</span></div>`;
    const pointColor =
      data_inicio.toLowerCase() === data[i][0].toLowerCase()
        ? 'point { size: 8; shape-type: circle; fill-color: #1BA65B; }'
        : i === data.length - 1
        ? 'point { size: 8; shape-type: circle; fill-color: #FC8E0E; }'
        : 'point { size: 3; shape-type: circle; fill-color: #2c8ad2; }';
    row.push(tooltipValue);
    row.push(pointColor);
    row.push(data[i][1].replace('.', ','));
    formattedData.push(row);
  }
  return formattedData;
};
